import React, { useEffect, useState } from "react";
import { ListChecks, Plus, Trash2, Link as LinkIcon, Upload, ExternalLink, FileText, Check, Edit2, X } from "lucide-react";
import { toast } from "sonner";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";

/**
 * Shows todos assigned by PM/RPM to the school/principal for a given event.
 * Used in EventDetail (PM/RPM compose side) AND PrincipalDashboard (principal response side).
 */
export default function SchoolTodosPanel({ event, mode = "manager" }) {
  const [todos, setTodos] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", expects: "text" });
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ title: "", description: "", expects: "text" });
  const { user } = useAuth();

  const load = async () => {
    const r = await api.get(`/events/${event.event_id}/school-todos`);
    setTodos(r.data);
  };
  useEffect(() => { load(); }, [event.event_id]);

  const add = async (e) => {
    e.preventDefault();
    await api.post(`/events/${event.event_id}/school-todos`, form);
    setForm({ title: "", description: "", expects: "text" });
    load();
  };

  const startEdit = (t) => {
    setEditingId(t.todo_id);
    setEditDraft({ title: t.title || "", description: t.description || "", expects: t.expects || "text" });
  };

  const cancelEdit = () => { setEditingId(null); setEditDraft({ title: "", description: "", expects: "text" }); };

  const saveEdit = async (t) => {
    if (!editDraft.title.trim()) return toast.error("Title is required");
    try {
      await api.patch(`/school-todos/${t.todo_id}`, editDraft);
      toast.success("To-do updated");
      cancelEdit();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const respondText = async (t, text) => {
    await api.patch(`/school-todos/${t.todo_id}`, { response_text: text, status: text ? "done" : "pending" });
    load();
  };

  const respondLink = async (t, url) => {
    await api.patch(`/school-todos/${t.todo_id}`, { response_url: url, status: url ? "done" : "pending" });
    load();
  };

  const uploadFile = async (t, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    const r = await api.post("/upload", fd);
    await api.patch(`/school-todos/${t.todo_id}`, { response_url: `${BACKEND_URL}${r.data.url}`, status: "done" });
    toast.success("Uploaded");
    load();
  };

  const del = async (t) => {
    if (!window.confirm("Delete this to-do?")) return;
    await api.delete(`/school-todos/${t.todo_id}`);
    load();
  };

  return (
    <div className="space-y-4">
      {mode === "manager" && (
        <form onSubmit={add} className="dashboard-card p-4">
          <div className="font-display font-bold mb-2 flex items-center gap-2"><ListChecks className="w-4 h-4" /> Assign new to-do to the school</div>
          <div className="grid sm:grid-cols-3 gap-2">
            <input required placeholder="Title (e.g., Send hall layout)" className="input-pill sm:col-span-2" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="todo-title" />
            <select className="input-pill" value={form.expects} onChange={(e) => setForm({ ...form, expects: e.target.value })}>
              <option value="text">Text reply</option>
              <option value="link">Link</option>
              <option value="file">File / Image</option>
            </select>
          </div>
          <textarea rows={2} placeholder="Description" className="input-pill mt-2" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <button type="submit" className="btn-primary w-full py-2.5 mt-2 text-sm" data-testid="todo-submit"><Plus className="w-3 h-3" /> Assign</button>
        </form>
      )}

      <div className="space-y-2">
        {todos.length === 0 && <div className="dashboard-card p-8 text-center text-slate-400 text-sm">No school to-dos yet.</div>}
        {todos.map((t) => (
          <div key={t.todo_id} className={`dashboard-card p-4 ${t.status === "done" ? "bg-emerald-50/40 border-emerald-200" : ""}`} data-testid={`todo-${t.todo_id}`}>
            {mode === "manager" && editingId === t.todo_id ? (
              <div className="space-y-2" data-testid={`todo-edit-form-${t.todo_id}`}>
                <div className="grid sm:grid-cols-3 gap-2">
                  <input placeholder="Title" className="input-pill py-2 text-sm sm:col-span-2" value={editDraft.title} onChange={(e) => setEditDraft({ ...editDraft, title: e.target.value })} data-testid={`todo-edit-title-${t.todo_id}`} />
                  <select className="input-pill py-2 text-sm" value={editDraft.expects} onChange={(e) => setEditDraft({ ...editDraft, expects: e.target.value })}>
                    <option value="text">Text reply</option>
                    <option value="link">Link</option>
                    <option value="file">File / Image</option>
                  </select>
                </div>
                <textarea rows={2} placeholder="Description" className="input-pill" value={editDraft.description} onChange={(e) => setEditDraft({ ...editDraft, description: e.target.value })} />
                <div className="flex gap-2">
                  <button onClick={() => saveEdit(t)} className="btn-primary py-1.5 text-xs" data-testid={`todo-save-${t.todo_id}`}><Check className="w-3 h-3" /> Save</button>
                  <button onClick={cancelEdit} className="btn-ghost py-1.5 text-xs"><X className="w-3 h-3" /> Cancel</button>
                </div>
              </div>
            ) : (
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {t.status === "done" ? <Check className="w-4 h-4 text-emerald-600" /> : <ListChecks className="w-4 h-4 text-orange-600" />}
                  <div className="font-bold text-slate-900">{t.title}</div>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{t.expects}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">Assigned by {t.assigned_by_name} · {t.created_at?.slice(0, 10)}</div>
                {t.description && <div className="text-sm text-slate-700 mt-1">{t.description}</div>}

                {t.status === "done" && (
                  <div className="mt-2 p-2 rounded-lg bg-white border border-emerald-100 text-sm">
                    {t.response_text && <div>{t.response_text}</div>}
                    {t.response_url && <a href={t.response_url} target="_blank" rel="noreferrer" className="text-orange-600 underline inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> View attachment / link</a>}
                  </div>
                )}

                {mode === "principal" && t.status !== "done" && (
                  <div className="mt-3">
                    {t.expects === "text" && <PrincipalTextResponse t={t} onSubmit={respondText} />}
                    {t.expects === "link" && <PrincipalLinkResponse t={t} onSubmit={respondLink} />}
                    {t.expects === "file" && (
                      <label className="btn-primary py-2 text-xs inline-flex cursor-pointer" data-testid={`todo-upload-${t.todo_id}`}>
                        <Upload className="w-3 h-3" /> Upload file
                        <input type="file" className="hidden" onChange={(e) => uploadFile(t, e.target.files?.[0])} />
                      </label>
                    )}
                  </div>
                )}
              </div>
              {mode === "manager" && (
                <div className="flex flex-col gap-1">
                  <button onClick={() => startEdit(t)} className="text-slate-500 hover:bg-slate-100 p-1 rounded" title="Edit" data-testid={`todo-edit-${t.todo_id}`}><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => del(t)} className="text-rose-500 hover:bg-rose-50 p-1 rounded" title="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              )}
            </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function PrincipalTextResponse({ t, onSubmit }) {
  const [v, setV] = useState("");
  return (
    <div className="flex gap-2">
      <input placeholder="Your reply" className="input-pill text-sm py-2" value={v} onChange={(e) => setV(e.target.value)} data-testid={`todo-text-${t.todo_id}`} />
      <button onClick={() => onSubmit(t, v)} className="btn-primary py-2 px-3 text-xs">Submit</button>
    </div>
  );
}
function PrincipalLinkResponse({ t, onSubmit }) {
  const [v, setV] = useState("");
  return (
    <div className="flex gap-2">
      <input placeholder="Paste link (https://…)" className="input-pill text-sm py-2" value={v} onChange={(e) => setV(e.target.value)} data-testid={`todo-link-${t.todo_id}`} />
      <button onClick={() => onSubmit(t, v)} className="btn-primary py-2 px-3 text-xs"><LinkIcon className="w-3 h-3" /></button>
    </div>
  );
}
